import {buildEntrySchemaFor} from '@github-ui/swp-core/schemas/contentful/entry'
import {AssetSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/asset'
import {RichTextSchema} from '@github-ui/swp-core/schemas/contentful/richText'
import {safeOptional} from '@github-ui/swp-core/schemas/safe-optional'
import * as z from 'zod'
import {DetailSectionSchema} from '../components/detailSection'

import {AssetItemSchema} from '../components/assetItem'
import {IconItemSchema} from '../components/iconItem'
import {IconLinkItemSchema} from '../components/iconLinkItem'
import {ImageLinkItemSchema} from '../components/imageLinkItem'
import {LinkSchema} from '../components/link'
import {MediaSchema} from '../components/media'
import {DetailsTemplateSettingsSchema} from '../components/detailsTemplateSettings'
import {FaqGroupSchema} from '../components/faqItem'

import {SubnavSchema} from '../components/subnav'
import {TextItemSchema} from '../components/textItem'
import {IntroContextEnum} from './templateFlexSuiteOverview'

export const DetailsHeroVariantEnum = z.enum(['Stacked', 'Carousel', 'Default', '50/50'])

export type DetailsHeroVariant = z.infer<typeof DetailsHeroVariantEnum>

export const StackedHeroPositionEnum = z.enum(['Default', 'Center', 'Fill'])

export type StackedHeroPosition = z.infer<typeof StackedHeroPositionEnum>

export const IntroLogoTypeEnum = z.enum(['Marquee', 'Tiles'])

export type IntroLogoType = z.infer<typeof IntroLogoTypeEnum>

export const OutroTestimonialEnum = z.enum(['Quote', 'Stats', 'Brand Banner'])

export type OutroTestimonial = z.infer<typeof OutroTestimonialEnum>

export const TemplateFlexSuiteDetailsSchema = buildEntrySchemaFor('templateFlexSuiteDetails', {
  fields: z.lazy(() =>
    z.object({
      title: z.string().optional(),
      featuredSections: safeOptional(z.array(DetailSectionSchema)),
      // Template Settings (e.g. branded experience)
      settings: safeOptional(DetailsTemplateSettingsSchema),
      // SubNav
      subNav: safeOptional(SubnavSchema),
      // Hero
      heroHeading: RichTextSchema,
      heroLabel: z.string().optional(),
      heroDescription: safeOptional(RichTextSchema),
      heroCtaOne: LinkSchema,
      heroCtaTwo: safeOptional(LinkSchema),
      heroTertiaryDescription: safeOptional(RichTextSchema),
      heroVariant: safeOptional(DetailsHeroVariantEnum),
      heroAsset: safeOptional(MediaSchema),
      heroPosition: safeOptional(StackedHeroPositionEnum),
      heroCarousel: safeOptional(z.array(AssetItemSchema)),
      // Intro
      introLogoHeading: z.string().optional(),
      introLogoType: safeOptional(IntroLogoTypeEnum),
      introLogos: safeOptional(z.array(z.string())),
      introLogoLink: safeOptional(LinkSchema),
      introContext: safeOptional(IntroContextEnum),
      introEyebrow: z.string().optional(),
      introContextHeading: safeOptional(RichTextSchema),
      introContextLink: safeOptional(LinkSchema),
      introContextBody: safeOptional(RichTextSchema),
      introItems: safeOptional(z.array(z.union([IconItemSchema, TextItemSchema]))),
      introIconCards: safeOptional(z.array(IconLinkItemSchema)),
      introPillars: safeOptional(z.array(IconItemSchema)),
      introImageCards: safeOptional(z.array(ImageLinkItemSchema)),
      introStats: safeOptional(z.array(TextItemSchema)),
      // Outro
      outroTestimonial: safeOptional(OutroTestimonialEnum),
      outroTestimonialLink: safeOptional(LinkSchema),
      outroTestimonialLogo: z.string().optional(),
      outroTestimonialQuote: safeOptional(RichTextSchema),
      outroTestimonialImage: safeOptional(AssetSchema),
      outroTestimonialLabel: z.string().optional(),
      outroTestimonialStats: safeOptional(z.array(TextItemSchema)),
      outroTestimonialName: z.string().optional(),
      outroTestimonialTitle: z.string().optional(),
      outroCustomerStories: safeOptional(z.array(IconLinkItemSchema)),
      outroCtaBanner: safeOptional(z.boolean()),
      outroCtaHeading: safeOptional(RichTextSchema),
      outroCtaDescription: safeOptional(RichTextSchema),
      outroCtaOne: safeOptional(LinkSchema),
      outroCtaTwo: safeOptional(LinkSchema),
      outroIncludeResources: safeOptional(z.boolean()),
      outroResourcesHeading: safeOptional(RichTextSchema),
      outroResources: safeOptional(z.array(IconLinkItemSchema)),
      includeFaqs: safeOptional(z.boolean()),
      faqHeading: safeOptional(RichTextSchema),
      outroFaqs: safeOptional(z.array(FaqGroupSchema)),
    }),
  ),
})

export type TemplateFlexSuiteDetails = z.infer<typeof TemplateFlexSuiteDetailsSchema>
